import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { Card } from "design-system/molecules/Card"
import DateDisplay from "design-system/molecules/DateDisplay"
import { cn } from "design-system/utils"
import { isNil } from "lopermut"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ChangeLogValuesByModel } from "./index"

type ProcessedLog = {
  id: string
  createdAt: string
  snapshot: Record<string, unknown>
  snapshotKeysMap: Array<{ label: string; key: string }>
  actorDisplay: React.ReactNode
}

type Props<T extends keyof ChangeLogValuesByModel> = {
  log: ProcessedLog
  onApplyChanges?: (snapshot: ChangeLogValuesByModel[T]) => Promise<void>
  canApplyChanges?: (snapshot: ChangeLogValuesByModel[T]) => boolean
  valueFormatter?: (key: string, value: unknown) => string | null
}

export function ChangeLogEntry<T extends keyof ChangeLogValuesByModel>({
  log,
  onApplyChanges,
  canApplyChanges,
  valueFormatter,
}: Props<T>) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpand = () => setIsExpanded((prev) => !prev)

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text
    return text.slice(0, maxLength) + "..."
  }

  return (
    <div className="relative pl-8">
      <div className="absolute top-1 left-0 flex size-4 items-center justify-center rounded-full bg-foreground border-2 border-background z-10" />
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">
              <DateDisplay date={log.createdAt} format="distance" />
            </span>
          </div>
          <div className="flex items-center gap-0 flex-wrap">
            <span className="text-sm text-muted-foreground">
              {t("shared.changeHistory.changedBy")}&nbsp;
            </span>
            {log.actorDisplay}
          </div>
        </div>
        <Card className="flex flex-col gap-4 p-4">
          {log.snapshotKeysMap.map(({ key, label }) => {
            const value = log.snapshot[key]
            const isEmpty = isNil(value) || value === ""
            const formattedValue = !isEmpty && valueFormatter ? valueFormatter(key, value) : null
            const valueStr = isEmpty
              ? t("shared.changeHistory.emptyValue")
              : (formattedValue ?? String(value))
            const hasLongContent = valueStr.length > 100

            return (
              <div key={key} className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {label}
                </span>
                <p
                  className={cn(
                    "text-sm leading-relaxed whitespace-pre-wrap break-words",
                    isEmpty && "text-muted-foreground",
                  )}
                >
                  {isExpanded || !hasLongContent ? valueStr : truncateText(valueStr)}
                </p>
                {hasLongContent && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 text-xs text-muted-foreground w-fit hover:text-primary hover:underline"
                    onClick={toggleExpand}
                  >
                    {isExpanded
                      ? t("shared.changeHistory.showLess")
                      : t("shared.changeHistory.showMore")}
                  </Button>
                )}
              </div>
            )
          })}
          {log.snapshotKeysMap.length === 0 && (
            <p className="text-sm text-muted-foreground">{t("shared.changeHistory.emptyValue")}</p>
          )}
          {onApplyChanges &&
            (!canApplyChanges || canApplyChanges(log.snapshot as ChangeLogValuesByModel[T])) && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button type="button" variant="outline" size="sm">
                    {t("shared.changeHistory.applyChanges")}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t("shared.changeHistory.applyChanges")}</DialogTitle>
                    <DialogDescription>
                      {t("shared.changeHistory.applyChangesConfirmation")}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">{t("shared.cancel")}</Button>
                    </DialogClose>
                    <DialogClose asChild>
                      <Button
                        variant="default"
                        onClick={() => onApplyChanges(log.snapshot as ChangeLogValuesByModel[T])}
                      >
                        {t("shared.confirm")}
                      </Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
        </Card>
      </div>
    </div>
  )
}
